/**
 * SendToTimelineButton — acción de envío al Timeline desde el preview de un nodo.
 *
 * La identidad del archivo es `assetId`; `url` (el snapshot del nodo) es sólo
 * fallback y la URL definitiva la resuelve el backend en cada click.
 *
 * Props:
 *   assetId      string (opcional) — id del asset persistido
 *   url          string (opcional) — URL guardada en el nodo (fallback)
 *   kind         'image' | 'video' | 'audio' (required)
 *   label        string (opcional)
 *   duration     number (opcional)
 *   thumbUrl     string (opcional)
 *   nodeId       string (opcional) — para persistir el assetId resuelto
 *   testIdPrefix string (opcional)
 */
import { useState } from 'react';
import { Film, Loader2 } from 'lucide-react';
import { useWorkflowStore } from '../store/workflowStore';
import { sendAssetToTimeline } from '../utils/sendAssetToTimeline';

const SendToTimelineButton = ({
  assetId,
  url,
  kind,
  label,
  duration,
  thumbUrl,
  nodeId,
  testIdPrefix = 'send-to-timeline',
}) => {
  const [busy, setBusy] = useState(false);
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);

  if ((!assetId && !url) || !kind) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await sendAssetToTimeline(assetId, {
        fallbackUrl: url || null,
        fallbackKind: kind,
        label,
        duration,
        thumbUrl,
        // E2 — trazabilidad: qué proyecto y qué nodo generaron este clip.
        source: nodeId
          ? { project_id: useWorkflowStore.getState().currentProjectId, node_id: nodeId }
          : null,
        onResolved: (asset) => {
          if (!nodeId) return;
          if (asset.id === assetId && asset.url === url) return;
          updateNodeData(nodeId, {
            assetId: asset.id,
            outputUrl: asset.url,
            outputType: asset.media_kind || kind,
          });
        },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      className="node-mini-timeline-btn"
      onClick={handleClick}
      disabled={busy}
      title="Enviar al Timeline Editor"
      data-testid={`${testIdPrefix}-btn`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Film size={12} />}
      Timeline
    </button>
  );
};

export default SendToTimelineButton;
